<template>
  <div class="file-uploader">
    <div 
      class="dropzone"
      :class="{ 'dropzone-active': isDragging, 'dropzone-error': error }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
      @click="$refs.fileInput.click()"
    >
      <input 
        ref="fileInput"
        type="file"
        accept=".csv"
        class="file-input"
        @change="onFileChange"
      >
      
      <div v-if="!file" class="dropzone-content">
        <div class="icon-upload">
          <svg width="48" height="48" viewBox="0 0 24 24">
            <path fill="currentColor" d="M14,13V17H10V13H7L12,8L17,13M19.35,10.03C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.03C2.34,8.36 0,10.9 0,14A6,6 0 0,0 6,20H19A5,5 0 0,0 24,15C24,12.36 21.95,10.22 19.35,10.03Z" />
          </svg>
        </div>
        <div class="dropzone-text">
          <div class="dropzone-title">{{ isDragging ? 'Drop CSV file here' : 'Click or drag CSV file here' }}</div>
          <div class="dropzone-subtitle">Only CSV files are supported</div>
        </div>
      </div>
      
      <div v-else class="file-info">
        <div class="file-icon">
          <svg width="32" height="32" viewBox="0 0 24 24">
            <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
          </svg>
        </div>
        <div class="file-details">
          <div class="file-name">{{ file.name }}</div>
          <div class="file-size">{{ formatFileSize(file.size) }}</div>
        </div>
        <button class="btn-icon" @click.stop="removeFile" title="Remove file">
          <span class="icon-close">×</span>
        </button>
      </div>
    </div>
    
    <div v-if="error" class="error-message" role="alert">
      {{ error }}
    </div>
    
    <div class="upload-actions" v-if="file">
      <button
        class="btn btn-primary"
        @click="upload"
        :disabled="loading"
        :aria-busy="loading"
      >
        <span v-if="loading" class="spinner-small" aria-hidden="true"></span>
        <span>{{ loading ? 'Uploading...' : 'Upload' }}</span>
        <span v-if="loading" class="sr-only">Uploading file, please wait</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  computed: {
    ...mapGetters([
      'schoolId',
      'studentImportFile',
      'studentImportDragging',
      'studentImportLoading',
      'studentImportError'
    ]),
    
    file: {
      get() {
        return this.studentImportFile;
      },
      set(value) {
        this.$store.dispatch('SET_STUDENT_IMPORT_FILE', value);
      }
    },
    
    isDragging: {
      get() {
        return this.studentImportDragging;
      },
      set(value) {
        this.$store.dispatch('SET_STUDENT_IMPORT_DRAGGING', value);
      }
    },
    
    loading() {
      return this.studentImportLoading;
    },
    
    error() {
      return this.studentImportError;
    }
  },
  
  methods: {
    onDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },
    
    onFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.validateAndSetFile(file);
      }
    },
    
    validateAndSetFile(file) {
      this.$store.dispatch('SET_STUDENT_IMPORT_ERROR', null);
      
      if (!file.name.endsWith('.csv')) {
        this.$store.dispatch('SET_STUDENT_IMPORT_ERROR', 'Only CSV files are supported');
        return;
      }
      
      this.file = file;
    },
    
    removeFile() {
      this.file = null;
      this.$store.dispatch('SET_STUDENT_IMPORT_ERROR', null);
      // Reset file input to allow selecting the same file again
      this.$refs.fileInput.value = '';
    },
    
    formatFileSize(bytes) {
      if (bytes < 1024) {
        return bytes + ' bytes';
      } else if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(1) + ' KB';
      } else {
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      }
    },
    
    upload() {
      if (!this.file) return;
      
      this.$store.dispatch('SET_STUDENT_IMPORT_LOADING', true);
      this.$store.dispatch('SET_STUDENT_IMPORT_ERROR', null);
      
      const formData = new FormData();
      formData.append('file', this.file);

      this.$store.dispatch('UPLOAD_STUDENT_IMPORT_FILE', {school_id: this.schoolId, formData: formData})
        .then(response => {
          this.$store.dispatch('SET_STUDENT_IMPORT_LOADING', false);
          this.$emit('uploaded', response.data);
        })
        .catch(error => {
          this.$store.dispatch('SET_STUDENT_IMPORT_LOADING', false);
          this.$store.dispatch('SET_STUDENT_IMPORT_ERROR', error.response?.data?.detail || error.response?.data?.error || 'Failed to upload file');
        });
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/css/partials/variables';

.file-uploader {
  width: 100%;
}

.dropzone {
  border: 2px dashed #ccc;
  border-radius: 4px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dropzone-active {
  border-color: $green;
  background-color: rgba($green, 0.05);
}

.dropzone-error {
  border-color: $red;
}

.file-input {
  display: none;
}

.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.icon-upload {
  color: #757575;
  margin-bottom: 16px;
}

.dropzone-title {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
}

.dropzone-subtitle {
  font-size: 14px;
  color: #757575;
}

.file-info {
  display: flex;
  align-items: center;
  width: 100%;
}

.file-icon {
  color: $green;
  margin-right: 16px;
}

.file-details {
  flex: 1;
  text-align: left;
}

.file-name {
  font-weight: 500;
  margin-bottom: 4px;
  word-break: break-all;
}

.file-size {
  font-size: 12px;
  color: #757575;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.icon-close {
  font-size: 20px;
  color: #757575;
  font-weight: bold;
}

.error-message {
  color: $red;
  font-size: 14px;
  margin-top: 8px;
  text-align: left;
}

.upload-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  transition: background-color 0.3s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: $green;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: $dark-green;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  margin-right: 8px;
  animation: spin 1s linear infinite;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 